<template>
  <div class="py-4 container-fluid">
    <div class="row mb-3">
      <div class="col-12 d-flex flex-wrap justify-content-between align-items-center">
        <div>
          <h5 class="mb-0">{{ $t('reports.endCustomerBillingTitle') }}</h5>
          <p class="mb-0 text-sm text-secondary">{{ $t('reports.endCustomerBillingSubtitle') }}</p>
        </div>
        <material-button variant="outline" color="dark" size="sm" :disabled="!rows.length" @click="onExport">
          <i class="material-icons text-sm me-1">download</i>{{ $t('reports.exportCsv') }}
        </material-button>
      </div>
    </div>

    <div class="card mb-3">
      <div class="card-body d-flex flex-wrap gap-3 align-items-end">
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.from') }}</label>
          <el-date-picker v-model="fromDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
        <div>
          <label class="text-xs text-secondary d-block mb-1">{{ $t('common.to') }}</label>
          <el-date-picker v-model="toDate" type="date" value-format="YYYY-MM-DD" format="DD/MM/YYYY" :clearable="false" @change="reload" />
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-secondary" role="status"></div>
        </div>
        <template v-else>
          <el-table :data="rows" stripe>
            <el-table-column prop="endCustomerName" :label="$t('reports.endCustomer')" min-width="180" />
            <el-table-column prop="projectCount" :label="$t('reports.projects')" align="center" min-width="100" />
            <el-table-column prop="reservationCount" :label="$t('reports.reservations')" align="center" min-width="110" />
            <el-table-column :label="$t('reports.internalRevenue')" align="right" min-width="130">
              <template #default="{ row }">{{ money(row.internalRevenue) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.externalRentRevenue')" align="right" min-width="130">
              <template #default="{ row }">{{ money(row.externalRevenue) }}</template>
            </el-table-column>
            <el-table-column :label="$t('reports.totalRevenue')" align="right" min-width="130">
              <template #default="{ row }"><strong>{{ money(row.totalRevenue) }}</strong></template>
            </el-table-column>
          </el-table>
          <div class="d-flex justify-content-between align-items-center mt-3">
            <span class="text-sm text-secondary">{{ $t('reports.totalRevenue') }}: <strong>{{ money(grandTotalRevenue) }}</strong></span>
            <el-pagination v-model:current-page="currentPage" :page-size="pageSize" :total="totalItems" layout="prev, pager, next" @current-change="fetchData" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../services/api";
import MaterialButton from "@/components/MaterialButton.vue";
import { exportCsv } from "../../utils/csvExport";

export default {
  name: "EndCustomerBillingReport",
  components: { MaterialButton },
  data() {
    const today = new Date();
    const from = new Date();
    from.setMonth(today.getMonth() - 6);
    return {
      fromDate: this.toIso(from),
      toDate: this.toIso(today),
      rows: [],
      totalItems: 0,
      grandTotalRevenue: 0,
      currentPage: 1,
      pageSize: 20,
      loading: true,
    };
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    toIso(d) {
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    },
    money(v) {
      return `${new Intl.NumberFormat("is-IS").format(Math.round(v || 0))} kr.`;
    },
    reload() {
      this.currentPage = 1;
      this.fetchData();
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await api.get("/Analytics/EndCustomer/Billing", {
          params: { fromDate: this.fromDate, toDate: this.toDate, currentPage: this.currentPage, pageSize: this.pageSize },
        });
        this.rows = response.data.result || [];
        this.totalItems = response.data.totalItems || 0;
        this.grandTotalRevenue = response.data.grandTotalRevenue || 0;
      } catch (e) {
        console.error("EndCustomerBillingReport fetchData error:", e); // eslint-disable-line no-console
        this.$toast.error(this.$t("common.unableToRetrieveData"), { timeout: 0 });
      } finally {
        this.loading = false;
      }
    },
    onExport() {
      const columns = [
        { key: "endCustomerName", label: this.$t("reports.endCustomer") },
        { key: "projectCount", label: this.$t("reports.projects") },
        { key: "reservationCount", label: this.$t("reports.reservations") },
        { key: "internalRevenue", label: this.$t("reports.internalRevenue") },
        { key: "externalRevenue", label: this.$t("reports.externalRentRevenue") },
        { key: "totalRevenue", label: this.$t("reports.totalRevenue") },
      ];
      const flat = this.rows.map((r) => ({ ...r, totalRevenue: r.internalRevenue + r.externalRevenue }));
      exportCsv(`end-customer-billing-${this.fromDate}_${this.toDate}`, columns, flat);
    },
  },
};
</script>
